/**
 * Dubbed Audio ZIP Strategy
 *
 * Creates ZIP archive with dubbed video and all audio tracks.
 * Implements IZipContentStrategy for extensibility.
 */

import type { IZipContentStrategy, ZipFileEntry } from '../types';

/**
 * Response from dubbing API with audio tracks
 */
export interface DubbingResultData {
  output_video_url?: string | null;
  audio_tracks?: Record<string, string> | null;
  job_id?: string;
  processing_time_seconds?: number | null;
  segments_processed?: number | null;
}

/**
 * Strategy for creating ZIP with dubbed video and all audio tracks.
 * Useful for users who need separate audio files for each language.
 */
export class DubbedAudioStrategy implements IZipContentStrategy {
  readonly type = 'dubbed_audio';

  /**
   * Build file entries from dubbing response.
   *
   * @param sourceData - DubbingResultData from dubbing API
   * @returns Array of file entries for ZIP
   */
  buildFileEntries(sourceData: unknown): ZipFileEntry[] {
    const response = sourceData as DubbingResultData;
    const entries: ZipFileEntry[] = [];

    // Add dubbed video file
    if (response.output_video_url) {
      const videoFilename = `dubbed_video_${response.job_id || 'download'}.mp4`;
      entries.push({
        url: response.output_video_url,
        filename: videoFilename,
        metadata: {
          type: 'video',
          dubbed: true,
        },
      });
    }

    // Add all audio tracks (one per language)
    if (response.audio_tracks && typeof response.audio_tracks === 'object') {
      for (const [langCode, url] of Object.entries(response.audio_tracks)) {
        if (url) {
          entries.push({
            url: url as string,
            filename: `audio_${langCode}.mp3`,
            folder: 'audio_tracks',
            metadata: {
              type: 'audio',
              language: langCode,
              format: 'mp3',
            },
          });
        }
      }
    }

    return entries;
  }

  /**
   * Get archive name based on task ID.
   */
  getArchiveName(sourceData: unknown): string {
    const response = sourceData as DubbingResultData;

    if (response.job_id) {
      return `dubbed_package_${response.job_id}`;
    }

    return `dubbed_package_${Date.now()}`;
  }

  /**
   * Generate manifest data for the ZIP.
   */
  getManifestData(sourceData: unknown): Record<string, unknown> {
    const response = sourceData as DubbingResultData;
    const audioLanguages = response.audio_tracks ? Object.keys(response.audio_tracks) : [];

    return {
      type: 'dubbed_package',
      video: {
        included: !!response.output_video_url,
        dubbed: true,
      },
      audioTracks: {
        count: audioLanguages.length,
        languages: audioLanguages,
      },
      processing: {
        jobId: response.job_id,
        processingTime: response.processing_time_seconds,
        segmentsProcessed: response.segments_processed,
      },
      generatedAt: new Date().toISOString(),
    };
  }
}

// Export singleton for convenience
export const dubbedAudioStrategy = new DubbedAudioStrategy();
